import type { ComponentProps, CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/utils";

type ThemeScreenshotStyle = CSSProperties & {
  "--homepage-screenshot-light": string;
  "--homepage-screenshot-dark": string;
};

interface HomepageScreenshotProps extends Omit<
  ComponentProps<"img">,
  "children"
> {
  chromeClassName?: string;
  chromeLabel?: ReactNode;
  frameClassName?: string;
  imageClassName?: string;
  darkSrc?: string;
  overlay?: ReactNode;
}

export function HomepageScreenshot({
  alt,
  darkSrc,
  decoding = "async",
  frameClassName,
  height,
  imageClassName,
  loading = "lazy",
  overlay,
  src,
  style,
  width,
  ...imageProps
}: HomepageScreenshotProps) {
  const themeScreenshotStyle: ThemeScreenshotStyle | undefined = darkSrc
    ? {
        ...style,
        aspectRatio:
          style?.aspectRatio ??
          (width && height ? `${width} / ${height}` : undefined),
        "--homepage-screenshot-light": `url(${JSON.stringify(src)})`,
        "--homepage-screenshot-dark": `url(${JSON.stringify(darkSrc)})`,
      }
    : undefined;

  return (
    <div
      className={cn(
        "bg-card relative overflow-hidden rounded-xl border p-2",
        frameClassName,
      )}
    >
      {overlay}
      {darkSrc ? (
        <div
          role={alt ? "img" : undefined}
          aria-label={alt || undefined}
          className={cn(
            "homepage-theme-screenshot w-full rounded-lg border bg-cover bg-top bg-no-repeat",
            imageClassName,
          )}
          style={themeScreenshotStyle}
        />
      ) : (
        <img
          {...imageProps}
          src={src}
          width={width}
          height={height}
          alt={alt}
          decoding={decoding}
          loading={loading}
          className={cn("h-auto w-full rounded-lg border", imageClassName)}
          style={style}
        />
      )}
    </div>
  );
}
